/**
 * Generic error reporting utility
 * 
 * This utility provides a centralized way to report errors (TypeScript errors, runtime errors, etc.)
 * to the backend API for tracking and monitoring purposes.
 */

import { getCookie } from 'cookies-next';
import { toast } from 'react-toastify';
import api from '@/lib/api';

export interface ErrorReportingOptions {
  /**
   * The error object (Error, TypeError, etc.)
   */
  error?: Error | unknown;
  
  /**
   * Custom error message (overrides error.message if provided)
   */
  message?: string;
  
  /**
   * Additional context about where/why the error occurred
   */
  context?: string;
  
  /**
   * The request path that caused the error (if applicable)
   */
  requestPath?: string;
  
  /**
   * Additional metadata to include in the error report
   */
  metadata?: Record<string, any>;
  
  /**
   * Whether to show a toast notification to the user (default: false)
   */
  showToast?: boolean;
  
  /**
   * Custom toast message (default: generic error message)
   */
  toastMessage?: string;
  
  /**
   * Whether to log the error to console (default: true)
   */
  logToConsole?: boolean;
}

/**
 * Reports an error to the backend API
 * 
 * @param options - Error reporting options
 * @returns Promise that resolves when the error has been reported
 * 
 * @example
 * // Report a TypeScript/runtime error
 * reportError({
 *   error: new TypeError('Cannot read property of undefined'),
 *   context: 'User profile component',
 *   showToast: true
 * });
 * 
 * @example
 * // Report a custom error message
 * reportError({
 *   message: 'Failed to save form data',
 *   context: 'Form submission handler',
 *   requestPath: '/v1/form/save',
 *   metadata: { formId: 123 }
 * });
 * 
 * @example
 * // Report error in try-catch block
 * try {
 *   // some code
 * } catch (error) {
 *   reportError({
 *     error,
 *     context: 'Data processing function',
 *     showToast: true
 *   });
 * }
 */
export async function reportError(options: ErrorReportingOptions = {}): Promise<void> {
  console.log('🔴 reportError: Function called', { 
    hasError: !!options.error,
    hasMessage: !!options.message,
    context: options.context 
  });
  
  // Early return if server-side
  if (typeof window === 'undefined') {
    console.warn('reportError: Skipping - server-side');
    return;
  }
  
  const {
    error,
    message,
    context,
    requestPath,
    metadata = {},
    showToast = false,
    toastMessage,
    logToConsole = true,
  } = options;

  // Early return if server-side
  if (typeof window === 'undefined') {
    console.warn('reportError: Skipping - server-side');
    return;
  }

  try {
    // Extract error message
    let errorMessage = message;
    
    if (!errorMessage && error) {
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && !Array.isArray(error)) {
        const err = error as Record<string, unknown>;
        const details = err.details as Record<string, unknown> | undefined;
        if (details && typeof details === 'object') {
          const title = details.title != null ? String(details.title) : '';
          const detail = details.detail != null ? String(details.detail) : '';
          errorMessage = title && detail ? `${title}: ${detail}` : detail || title || String(error);
        } else if (err.message != null && typeof err.message === 'string') {
          errorMessage = err.message;
        } else if (err.error != null && typeof err.error === 'string') {
          errorMessage = err.error;
        } else {
          errorMessage = String(error);
        }
      } else {
        errorMessage = String(error);
      }
    }
    
    if (!errorMessage) {
      errorMessage = 'Unknown error occurred';
    }

    // Get user information
    const token = getCookie("token");
    const caUserId = getCookie("ca_user_id");
    const userEmail = getCookie("email");
    const userFullName = getCookie("user_full_name");

    // Determine which API endpoint to use
    const errorApiEndpoint = token 
      ? "/v1/care_ascendium_error/new"
      : "/v1/care_ascendium_error_outside/new";

    // Get current page path
    const currentPage = typeof window !== 'undefined' 
      ? window.location.pathname 
      : 'unknown';

    // Build error description
    let errorDescription = errorMessage;
    
    if (context) {
      errorDescription = `${context}: ${errorMessage}`;
    }

    // Add error stack if available
    if (error instanceof Error && error.stack) {
      errorDescription += `\n\nStack Trace:\n${error.stack}`;
    }

    // Add metadata if provided
    if (Object.keys(metadata).length > 0) {
      try {
        const metadataStr = JSON.stringify(metadata);
        errorDescription += `\n\nAdditional Metadata:\n${metadataStr}`;
      } catch (e) {
        errorDescription += `\n\nAdditional Metadata: [Unable to stringify metadata]`;
      }
    }

    // Add timestamp
    errorDescription += `\n\nTimestamp: ${new Date().toISOString()}`;

    // Get user IP address (with timeout to not delay error reporting)
    let userIpAddress = 'Unknown';
    try {
      // Try to get IP from a public service with 2 second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      const ipResponse = await fetch('https://api.ipify.org?format=json', {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      
      if (ipResponse.ok) {
        const ipData = await ipResponse.json();
        userIpAddress = ipData.ip || 'Unknown';
      }
    } catch (ipError: any) {
      // If IP fetch fails or times out, continue without it
      if (ipError.name !== 'AbortError') {
        console.warn('Failed to fetch IP address:', ipError);
      }
    }

    // Append IP address to error description
    errorDescription += `\n\nIP Address: ${userIpAddress}`;

    // Sanitize single quotes for database storage
    errorDescription = errorDescription.replace(/'/g, '"');

    // Prepare error payload
    const errorPayload = {
      p_request_path: requestPath || 'N/A',
      p_current_page: currentPage,
      p_ca_app: "Web",
      p_error_desc: errorDescription,
      p_ca_user_id: caUserId || null,
      p_ca_user_email: userEmail || null,
      p_device_info: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
    };

    // Log to console if enabled
    if (logToConsole) {
      console.error('Error reported:', {
        message: errorMessage,
        context,
        requestPath,
        metadata,
        error: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack,
        } : error,
      });
    }

    // Report error to API using the main api instance (same as existing working code)
    // The api interceptor checks requestPath != errorApiEndpoint to prevent infinite loops
    console.log('🔴 reportError: Making API call', {
      endpoint: errorApiEndpoint,
      hasToken: !!token,
    });
    
    try {
      const response = await api.post(errorApiEndpoint, errorPayload);
      console.log('✅ reportError: API call SUCCESS', response.data);
    } catch (apiError: any) {
      console.error('❌ reportError: API call FAILED', {
        message: apiError?.message,
        status: apiError?.response?.status,
        data: apiError?.response?.data,
      });
      // Don't throw - we don't want error reporting failures to break the app
    }

    // Show toast notification if requested
    if (showToast) {
      const messageToShow = toastMessage || 
        "We are very sorry for the inconvenience. The app encountered an error. We have informed the administrator. Please try again later.";
      
      toast.error(messageToShow, {
        toastId: `error-${Date.now()}`,
        autoClose: 5000,
      });
    }
  } catch (reportingError) {
    // If error reporting itself fails, log to console
    console.error('Failed to report error to API:', reportingError);
    console.error('Original error:', error || message);
  }
}

/**
 * Convenience function to report TypeScript/runtime errors
 * 
 * @param error - The error object
 * @param context - Optional context about where the error occurred
 * @param showToast - Whether to show a toast notification (default: false)
 * 
 * @example
 * try {
 *   // some code that might throw
 * } catch (error) {
 *   reportTypeScriptError(error, 'Component render', true);
 * }
 */
export function reportTypeScriptError(
  error: Error | unknown,
  context?: string,
  showToast: boolean = false
): void {
  reportError({
    error,
    context,
    showToast,
  }).catch((reportingError) => {
    console.error('Failed to report TypeScript error:', reportingError);
  });
}

/**
 * Convenience function to report API errors
 * 
 * @param error - The error object
 * @param requestPath - The API endpoint that caused the error
 * @param showToast - Whether to show a toast notification (default: true)
 * 
 * @example
 * api.get('/v1/data')
 *   .catch((error) => {
 *     reportApiError(error, '/v1/data', true);
 *   });
 */
export function reportApiError(
  error: Error | unknown,
  requestPath?: string,
  showToast: boolean = true
): void {
  reportError({
    error,
    requestPath,
    context: 'API Request',
    showToast,
  }).catch((reportingError) => {
    console.error('Failed to report API error:', reportingError);
  });
}

/**
 * Convenience function to report custom errors with a message
 * 
 * @param message - The error message
 * @param context - Optional context about where the error occurred
 * @param metadata - Optional additional metadata
 * @param showToast - Whether to show a toast notification (default: false)
 * 
 * @example
 * if (!data) {
 *   reportCustomError(
 *     'Data validation failed',
 *     'Form submission',
 *     { formId: 123, field: 'email' },
 *     true
 *   );
 * }
 */
export function reportCustomError(
  message: string,
  context?: string,
  metadata?: Record<string, any>,
  showToast: boolean = false
): void {
  reportError({
    message,
    context,
    metadata,
    showToast,
  }).catch((reportingError) => {
    console.error('Failed to report custom error:', reportingError);
  });
}

/**
 * Test function to verify error reporting is working
 * Call this from browser console: window.testErrorReporting()
 */
export function testErrorReporting(): void {
  console.log('🧪 Testing error reporting...');
  reportError({
    error: new Error('Test error from errorReporting utility'),
    context: 'Error Reporting Test',
    metadata: { test: true, timestamp: new Date().toISOString() },
    showToast: false,
  })
    .then(() => {
      console.log('✅ Test error reporting completed successfully');
    })
    .catch((error) => {
      console.error('❌ Test error reporting failed:', error);
    });
}

// Make test function available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).testErrorReporting = testErrorReporting;
  console.log('✅ testErrorReporting function available at window.testErrorReporting()');
  
  // Also expose reportError directly for testing
  (window as any).reportErrorDirect = reportError;
  console.log('✅ reportError function also available at window.reportErrorDirect()');
}
